/**
 * Pure hulpjes voor het leveranciersoverzicht (23 okt 2026). Los van het scherm, zodat
 * ze te testen zijn. Identiek op develop en main.
 */

/** multipleSelects kan als array óf komma-string binnenkomen (CLAUDE.md) — altijd een array. */
export const alsLijst = (w) => {
  if (Array.isArray(w)) return w.filter(Boolean).map(String);
  if (typeof w === 'string' && w) return w.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

export const LEEG_FILTER = {
  zoek: '', functie: '', specialisatie: '', type: '', status: 'actief', minScore: '', favoriet: false,
};

export const MIN_SCORES = [
  { waarde: '', label: 'Elke beoordeling' },
  { waarde: '3', label: '3 of hoger' },
  { waarde: '3.5', label: '3,5 of hoger' },
  { waarde: '4', label: '4 of hoger' },
  { waarde: '4.5', label: '4,5 of hoger' },
];

/** Zoeken gaat ook over functies en plaats: "camera" vindt Cameraman en Camera Operator. */
export function filterLeveranciers(rijen, f = LEEG_FILTER) {
  const z = (f.zoek || '').trim().toLowerCase();
  const min = f.minScore === '' || f.minScore == null ? null : Number(f.minScore);
  return (rijen || []).filter((r) => {
    const functies = alsLijst(r.functies);
    if (f.functie && !functies.includes(f.functie)) return false;
    if (f.specialisatie && !alsLijst(r.specialisaties).includes(f.specialisatie)) return false;
    if (f.type && r.categorie !== f.type) return false;
    if (f.status === 'actief' && !r.actief) return false;
    if (f.status === 'archief' && r.actief) return false;
    if (f.favoriet && !r.favoriet) return false;
    if (min != null && !(typeof r.score === 'number' && r.score >= min)) return false;
    if (z) {
      const hooi = [r.naam, r.bedrijf, r.email, r.plaats, ...functies].filter(Boolean).join(' ').toLowerCase();
      if (!hooi.includes(z)) return false;
    }
    return true;
  });
}

/**
 * Zoeken met "Status: Actief" verbergt gearchiveerde mensen. Hoeveel gearchiveerde treffers
 * zijn er voor dezelfde zoekopdracht? Alleen bij een zoekterm (anders is het geen "treffer").
 */
export function gearchiveerdeTreffers(rijen, f = LEEG_FILTER) {
  if (!(f.zoek || '').trim() || f.status !== 'actief') return 0;
  return filterLeveranciers(rijen, { ...f, status: 'archief' }).length;
}

const waardeTekst = (w) => {
  if (w == null || w === '' || (Array.isArray(w) && !w.length)) return '—';
  return Array.isArray(w) ? w.join(', ') : String(w);
};

/** Wijzigingen van heractiveren → regels "Label: oud → nieuw" voor de bevestiging. */
export function wijzigingRegels(wijzigingen, labels = {}) {
  return (wijzigingen || []).map(({ veld, oud, nieuw }) => {
    const label = labels[veld] || veld;
    if (veld === 'Opmerkingen') return `${label} wordt:\n${waardeTekst(nieuw)}`;
    return `${label}: ${waardeTekst(oud)} → ${waardeTekst(nieuw)}`;
  });
}

export const euro = (n) => (typeof n === 'number' ? `€ ${n.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}` : null);

export function tariefTekst(r) {
  const delen = [];
  if (typeof r?.dagtarief === 'number') delen.push(`${euro(r.dagtarief)} p/d`);
  if (typeof r?.uurtarief === 'number') delen.push(`${euro(r.uurtarief)} p/u`);
  return delen.join(' · ');
}

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** '2026-10-06' → '6 okt 2026'. Datumstrings, geen Date-objecten: geen tijdzoneschuif. */
export function datumKort(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return null;
  return `${Number(m[3])} ${MAANDEN[Number(m[2]) - 1]} ${m[1]}`;
}

export const scoreTekst = (s) => (typeof s === 'number' ? s.toFixed(1).replace('.', ',') : null);

export const kwartaalTekst = (k) => (/^\d{4}-Q[1-4]$/.test(k || '') ? `${k.slice(5)} ${k.slice(0, 4)}` : k);

/** Functies die bij een leverancier passen (Airtable-opties, interne rollen weggelaten). */
export const FUNCTIE_OPTIES = [
  'Cameraman', 'Camera Operator', 'Camera-journalist', 'Cinematograaf / DOP', 'Editor', 'Animator',
  'Illustrator', 'Regisseur', 'Copywriter', 'Creatief Strateeg', 'Researcher', 'Producer',
  'Productie-assistent', 'Opnameleider', 'Content Creator', 'Social Media Manager', 'Marketing',
  'Art Director', 'Setdresser', 'Stylist', 'Visagist', 'Colorist / Grader', 'Geluidstechnicus',
  'Gaffer', 'Fotograaf', 'Presentator / Host', 'Presentator / Host (Podcast)', 'Voice-over Artist',
  'Casting', 'Projectleider', 'Planner', 'Accountmanager',
];

export const LEEG_NIEUW = {
  naam: '', bedrijf: '', categorie: 'Freelancer', niveau: '', functies: [], specialisaties: [],
  portfolio: '', email: '', telefoon: '', plaats: '', dagtarief: '', uurtarief: '', opmerking: '',
};

/** Formulier → body voor POST /Api/leveranciers/nieuw: lege velden weg, bedragen als getal. */
export function nieuwNaarBody(f) {
  const body = {};
  for (const [k, v] of Object.entries(f || {})) {
    if (Array.isArray(v)) {
      const lijst = v.map((x) => String(x).trim()).filter(Boolean);
      if (lijst.length) body[k] = lijst;
    } else if (typeof v === 'string' && v.trim()) {
      body[k] = v.trim();
    }
  }
  for (const k of ['dagtarief', 'uurtarief']) {
    if (body[k] != null) {
      // "1.250,00" en "€ 650,5" → getal; lukt dat niet, dan gaat de tekst mee en weigert de server hem.
      let t = String(body[k]).replace(/[€\s]/g, '');
      if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
      const n = Number(t);
      body[k] = t !== '' && Number.isFinite(n) ? n : body[k];
    }
  }
  return body;
}
